import { useEffect, useState } from 'react';

type MatchFn<T> = (value: T, match: T) => boolean;

const defaultIsMatch = <T>(value: T, match: T) => value === match;

/**
 * Returns a delayed version of `value`. Transitions **to** `match` are applied
 * immediately; transitions **away** from `match` are held back by `delay` ms.
 *
 * Useful for loading indicators that should stay visible briefly after a fetch
 * completes, avoiding a flash of stale content before new results render.
 *
 * @example
 * // Show spinner immediately when fetching; keep it for 300ms after fetch ends
 * const fetchingState = useDelayedMatch(isFetching, 300, true);
 * const showSpinner = fetchingState === true;
 */
export function useDelayedMatch<T>(
  value: T,
  match: T,
  delay = 250,
  isMatch: MatchFn<T> = defaultIsMatch,
) {
  const [delayedValue, setDelayedValue] = useState(value);

  useEffect(() => {
    if (isMatch(value, match)) {
      setDelayedValue(value);

      return;
    }

    const timer = setTimeout(() => setDelayedValue(value), delay);

    return () => clearTimeout(timer);
  }, [value, match, delay, isMatch]);

  return delayedValue;
}
